/**
 * WorkflowBuilder — Drag-and-drop workflow editor with React Flow
 * Tier 1 skills only (NOTIFY, FORMAT, ROUTE, MATCH, INGEST, basic CALCULATE)
 * Tier 2 skills (RESOLVE, RESEARCH, VALIDATE, DETECT) require AW team
 */
import React, { useState, useCallback, useRef, useEffect, useMemo } from 'react';
import {
  ReactFlow,
  Background,
  MiniMap,
  Controls,
  useNodesState,
  useEdgesState,
  addEdge,
  Handle,
  Position,
  MarkerType,
  useReactFlow,
  ReactFlowProvider,
  type Node,
  type Edge,
  type Connection,
} from '@xyflow/react';
import '@xyflow/react/dist/style.css';
import api from '../api';

// ─── Tier classification ─────────────────────────────────────

const TIER1_CATEGORIES = new Set(['NOTIFY', 'FORMAT', 'ROUTE', 'INGEST', 'MATCH']);
const TIER2_CATEGORIES = new Set(['RESOLVE', 'RESEARCH', 'VALIDATE', 'DETECT']);
const TIER1_CALCULATE_OPS = new Set(['sum', 'count', 'average', 'min', 'max', 'ratio']);
const TIER2_CALCULATE_OPS = new Set(['variance', 'allocation', 'accrual', 'fx_revaluation', 'depreciation', 'interest']);

function isTier1Skill(skill: any): boolean {
  const cat = skill.category || skill.skill_category || '';
  if (TIER1_CATEGORIES.has(cat)) return true;
  if (cat === 'CALCULATE') {
    const op = skill.execution_config?.operation || skill.operation || '';
    return TIER1_CALCULATE_OPS.has(op) || (!TIER2_CALCULATE_OPS.has(op) && !op);
  }
  return false;
}

// ─── Colors ──────────────────────────────────────────────────

const ENGINE_COLORS: Record<string, string> = {
  CALCULATE: 'var(--accent)', DETECT: '#F59E0B', VALIDATE: '#10B981',
  FORMAT: '#A78BFA', MATCH: '#FB923C', RESEARCH: '#EC4899',
  NOTIFY: '#F87171', ROUTE: '#38BDF8', INGEST: '#34D399', RESOLVE: '#FBBF24',
};

const DEPARTMENTS = [
  'GL', 'AP', 'AR', 'TREASURY', 'INTERCOMPANY', 'FPA', 'COST_ACCOUNTING',
  'FIXED_ASSETS', 'TAX', 'PAYROLL', 'COMPLIANCE', 'SSC', 'TRAVEL_EXPENSE',
];

const TRIGGERS = ['DATA_UPLOAD', 'MANUAL', 'SCHEDULED', 'EVENT'];

function humanize(code: string): string {
  return code.replace(/_/g, ' ').replace(/\b\w/g, c => c.toUpperCase());
}

// ─── Custom Node ─────────────────────────────────────────────

const NODE_WIDTH = 200;
const NODE_HEIGHT = 80;

interface BuilderNodeData {
  stepNumber: number;
  skillCode: string;
  skillName: string;
  category: string;
  categoryColor: string;
  onSelect: (id: string) => void;
  [key: string]: any;
}

function BuilderSkillNode({ id, data }: { id: string; data: BuilderNodeData }) {
  return (
    <div
      onClick={() => data.onSelect(id)}
      style={{
        background: 'var(--bg-surface, var(--bg-surface))',
        border: '1px solid rgba(0,0,0,0.06)',
        borderLeft: `3px solid ${data.categoryColor}`,
        borderRadius: 8, padding: '10px 14px', width: NODE_WIDTH, cursor: 'pointer',
      }}
    >
      <div style={{ fontSize: 9, textTransform: 'uppercase' as const, letterSpacing: '0.5px', fontWeight: 600, color: data.categoryColor, marginBottom: 4 }}>
        {data.category}
      </div>
      <div style={{ fontSize: 12, fontWeight: 500, color: 'var(--text-primary, #F0F2F5)', marginBottom: 4, lineHeight: 1.3, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' as const }}>
        {data.skillName}
      </div>
      <div style={{ fontSize: 9, color: 'var(--text-hint, #475569)' }}>Step #{data.stepNumber}</div>
      <Handle type="target" position={Position.Left} style={{ background: 'var(--border-default)', border: 'none', width: 8, height: 8 }} />
      <Handle type="source" position={Position.Right} style={{ background: 'var(--border-default)', border: 'none', width: 8, height: 8 }} />
    </div>
  );
}

function EndNode() {
  return (
    <div style={{
      background: 'var(--bg-elevated, var(--bg-elevated))', border: '1px solid rgba(16,185,129,0.3)',
      borderRadius: '50%', width: 56, height: 56,
      display: 'flex', alignItems: 'center', justifyContent: 'center',
      fontSize: 10, fontWeight: 600, color: '#10B981',
    }}>
      END
      <Handle type="target" position={Position.Left} style={{ background: 'transparent', border: 'none' }} />
    </div>
  );
}

const nodeTypes = { builderSkill: BuilderSkillNode, end: EndNode };

// ─── Validation ──────────────────────────────────────────────

interface ValidationResult {
  label: string;
  pass: boolean;
  detail?: string;
}

function validateWorkflow(
  nodes: Node[],
  edges: Edge[],
  name: string,
  department: string,
): ValidationResult[] {
  const results: ValidationResult[] = [];
  const skillNodes = nodes.filter(n => n.type === 'builderSkill');

  // 1. At least 1 node
  results.push({
    label: 'At least 1 step',
    pass: skillNodes.length > 0,
  });

  // 2. Start node exists (no incoming edges)
  const targets = new Set(edges.map(e => e.target));
  const startNodes = skillNodes.filter(n => !targets.has(n.id));
  results.push({
    label: 'Start node defined',
    pass: startNodes.length > 0,
    detail: startNodes.length === 0 ? 'Every node has incoming edges' : undefined,
  });

  // 3. End path exists (at least one edge targets END)
  const hasEnd = edges.some(e => e.target === 'END');
  results.push({
    label: 'End path exists',
    pass: hasEnd || skillNodes.length === 0,
    detail: !hasEnd && skillNodes.length > 0 ? 'Connect at least one node to END' : undefined,
  });

  // 4. No orphan nodes
  const connectedNodes = new Set([...edges.map(e => e.source), ...edges.map(e => e.target)]);
  const orphans = skillNodes.filter(n => !connectedNodes.has(n.id));
  results.push({
    label: 'All nodes connected',
    pass: orphans.length === 0 || skillNodes.length <= 1,
    detail: orphans.length > 0 ? `Orphan nodes: ${orphans.map(n => (n.data as any).skillName).join(', ')}` : undefined,
  });

  // 5. Workflow name
  results.push({
    label: 'Workflow name set',
    pass: name.trim().length >= 3,
  });

  // 6. Department selected
  results.push({
    label: 'Department selected',
    pass: !!department,
  });

  return results;
}

// ─── Inner Component (needs ReactFlowProvider) ───────────────

interface WorkflowBuilderInnerProps {
  onClose: () => void;
}

function WorkflowBuilderInner({ onClose }: WorkflowBuilderInnerProps) {
  const reactFlowInstance = useReactFlow();

  // Skill palette data
  const [allSkills, setAllSkills] = useState<any[]>([]);
  const [skillsLoading, setSkillsLoading] = useState(true);
  const [expandedCats, setExpandedCats] = useState<Set<string>>(new Set(['NOTIFY', 'FORMAT']));

  // Canvas state
  const [nodes, setNodes, onNodesChange] = useNodesState([] as Node[]);
  const [edges, setEdges, onEdgesChange] = useEdgesState([] as Edge[]);
  const [selectedNodeId, setSelectedNodeId] = useState<string | null>(null);
  const nodeCountRef = useRef(0);

  // Toolbar state
  const [wfName, setWfName] = useState('');
  const [wfDept, setWfDept] = useState('');
  const [wfTrigger, setWfTrigger] = useState('MANUAL');
  const [wfDesc, setWfDesc] = useState('');

  // Validation
  const [validationResults, setValidationResults] = useState<ValidationResult[] | null>(null);

  // Save state
  const [saving, setSaving] = useState(false);
  const [saveResult, setSaveResult] = useState<{ ok: boolean; message: string } | null>(null);

  // Request form
  const [showRequestForm, setShowRequestForm] = useState(false);
  const [reqName, setReqName] = useState('');
  const [reqDesc, setReqDesc] = useState('');
  const [reqDept, setReqDept] = useState('');
  const [reqCaps, setReqCaps] = useState<string[]>([]);
  const [reqSubmitting, setReqSubmitting] = useState(false);
  const [reqResult, setReqResult] = useState('');

  // Load skills
  useEffect(() => {
    (async () => {
      try {
        const res = await api.get('/skills/registry/', { params: { limit: 1000, active_only: true } });
        setAllSkills(res.data.skills || []);
      } catch (e) {
        console.error('Failed to load skills:', e);
      } finally {
        setSkillsLoading(false);
      }
    })();
  }, []);

  // Filter to Tier 1 and group
  const tier1Skills = useMemo(() => allSkills.filter(isTier1Skill), [allSkills]);
  const groupedSkills = useMemo(() => {
    const groups: Record<string, any[]> = {};
    for (const s of tier1Skills) {
      const cat = s.category || s.skill_category || 'OTHER';
      if (!groups[cat]) groups[cat] = [];
      groups[cat].push(s);
    }
    // Sort categories by count desc
    return Object.entries(groups).sort((a, b) => b[1].length - a[1].length);
  }, [tier1Skills]);

  // Add END node on mount
  useEffect(() => {
    setNodes([{
      id: 'END',
      type: 'end',
      position: { x: 600, y: 200 },
      data: {},
      draggable: true,
    }]);
  }, [setNodes]);

  // ─── Handlers ────────────────────────────────────────────

  const onDragOver = useCallback((event: React.DragEvent) => {
    event.preventDefault();
    event.dataTransfer.dropEffect = 'move';
  }, []);

  const onDrop = useCallback((event: React.DragEvent) => {
    event.preventDefault();
    const raw = event.dataTransfer.getData('application/json');
    if (!raw) return;

    const skillData = JSON.parse(raw);
    const position = reactFlowInstance.screenToFlowPosition({
      x: event.clientX,
      y: event.clientY,
    });

    nodeCountRef.current += 1;
    const stepNum = nodeCountRef.current;
    const cat = skillData.category || skillData.skill_category || '';

    const newNode: Node = {
      id: `step-${stepNum}`,
      type: 'builderSkill',
      position,
      data: {
        stepNumber: stepNum,
        skillCode: skillData.skill_code,
        skillName: skillData.skill_name || humanize(skillData.skill_code),
        category: cat,
        categoryColor: ENGINE_COLORS[cat] || '#64748B',
        onSelect: setSelectedNodeId,
      },
    };

    setNodes(prev => [...prev, newNode]);
    setSaveResult(null);
    setValidationResults(null);
  }, [reactFlowInstance, setNodes]);

  const onConnect = useCallback((params: Connection) => {
    const newEdge = {
      ...params,
      type: 'smoothstep',
      style: { stroke: '#10B981', strokeWidth: 2 },
      markerEnd: { type: MarkerType.ArrowClosed, color: '#10B981', width: 14, height: 14 },
      animated: false,
    };
    setEdges(prev => addEdge(newEdge, prev));
    setSaveResult(null);
    setValidationResults(null);
  }, [setEdges]);

  const deleteNode = useCallback((nodeId: string) => {
    if (nodeId === 'END') return;
    setNodes(prev => prev.filter(n => n.id !== nodeId));
    setEdges(prev => prev.filter(e => e.source !== nodeId && e.target !== nodeId));
    setSelectedNodeId(null);
    setSaveResult(null);
    setValidationResults(null);
  }, [setNodes, setEdges]);

  const handleValidate = () => {
    const results = validateWorkflow(nodes, edges, wfName, wfDept);
    setValidationResults(results);
  };

  const handleSaveDraft = async () => {
    // Run validation first
    const results = validateWorkflow(nodes, edges, wfName, wfDept);
    setValidationResults(results);
    const hasBlocker = results.some(r => !r.pass);
    if (hasBlocker) {
      setSaveResult({ ok: false, message: 'Fix validation errors before saving' });
      return;
    }

    setSaving(true);
    try {
      const skillNodes = nodes.filter(n => n.type === 'builderSkill');

      // Build steps from nodes + edges
      const steps = skillNodes.map(n => {
        const data = n.data as BuilderNodeData;
        // Find outgoing edge
        const outEdge = edges.find(e => e.source === n.id);
        let onSuccess: number | null = null;
        if (outEdge) {
          if (outEdge.target === 'END') {
            onSuccess = null;
          } else {
            const targetNode = skillNodes.find(sn => sn.id === outEdge.target);
            if (targetNode) onSuccess = (targetNode.data as BuilderNodeData).stepNumber;
          }
        }

        return {
          step_number: data.stepNumber,
          skill_code: data.skillCode,
          on_success: onSuccess,
          on_failure: 'END',
          config: {},
        };
      });

      const res = await api.post('/skill-workflows/definitions', {
        name: wfName.trim(),
        department: wfDept,
        trigger_type: wfTrigger,
        description: wfDesc,
        steps,
      });
      setSaveResult({ ok: true, message: `Saved as ${res.data.workflow_code} (${res.data.step_count} steps)` });
    } catch (e: any) {
      const detail = e.response?.data?.detail || e.message || 'Save failed';
      setSaveResult({ ok: false, message: typeof detail === 'string' ? detail : JSON.stringify(detail) });
    } finally {
      setSaving(false);
    }
  };

  const handleClear = () => {
    if (nodes.filter(n => n.type === 'builderSkill').length === 0) return;
    if (!window.confirm('Clear all nodes from the canvas?')) return;
    setNodes([{ id: 'END', type: 'end', position: { x: 600, y: 200 }, data: {}, draggable: true }]);
    setEdges([]);
    nodeCountRef.current = 0;
    setSelectedNodeId(null);
    setValidationResults(null);
    setSaveResult(null);
  };

  const handleRequestSubmit = async () => {
    if (!reqName.trim() || !reqDesc.trim() || !reqDept) return;
    setReqSubmitting(true);
    try {
      await api.post('/skill-workflows/requests', {
        name: reqName.trim(),
        description: reqDesc.trim(),
        department: reqDept,
        requested_capabilities: reqCaps,
        priority: 'NORMAL',
      });
      setReqResult('Request submitted successfully. The Agent Workforce team will review it.');
      setReqName(''); setReqDesc(''); setReqDept(''); setReqCaps([]);
    } catch (e: any) {
      setReqResult('Failed to submit request. Please try again.');
    } finally {
      setReqSubmitting(false);
    }
  };

  const selectedNode = nodes.find(n => n.id === selectedNodeId);
  const selectedData = selectedNode?.data as BuilderNodeData | undefined;

  // ─── Palette drag start ──────────────────────────────────

  const onPaletteDragStart = (event: React.DragEvent, skill: any) => {
    event.dataTransfer.setData('application/json', JSON.stringify(skill));
    event.dataTransfer.effectAllowed = 'move';
  };

  // ─── Render ──────────────────────────────────────────────

  const inputStyle: React.CSSProperties = {
    background: 'var(--bg-elevated, var(--bg-elevated))',
    border: '1px solid var(--border-default, rgba(0,0,0,0.06))',
    borderRadius: 6, padding: '6px 10px', fontSize: 12,
    color: 'var(--text-primary, #F0F2F5)', outline: 'none', width: '100%',
  };

  const selectStyle: React.CSSProperties = {
    ...inputStyle, appearance: 'none' as const, cursor: 'pointer',
    backgroundImage: `url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='12' height='12' viewBox='0 0 24 24'%3E%3Cpath fill='%2394A3B8' d='M7 10l5 5 5-5z'/%3E%3C/svg%3E")`,
    backgroundRepeat: 'no-repeat', backgroundPosition: 'right 8px center',
    paddingRight: 28,
  };

  return (
    <div style={{ display: 'flex', flexDirection: 'column', height: 'calc(100vh - 200px)', minHeight: 500 }}>
      {/* Toolbar */}
      <div style={{
        display: 'flex', alignItems: 'center', gap: 8, padding: '10px 16px', flexWrap: 'wrap',
        background: 'var(--bg-surface, var(--bg-surface))', borderBottom: '1px solid var(--border-subtle)',
        borderRadius: '8px 8px 0 0',
      }}>
        <button onClick={onClose} style={{
          background: 'var(--bg-elevated)', border: '1px solid var(--border-default)',
          borderRadius: 6, padding: '5px 12px', fontSize: 11, color: 'var(--text-secondary)',
          cursor: 'pointer', fontWeight: 500,
        }}>&larr; Back</button>

        <input
          placeholder="Workflow Name"
          value={wfName}
          onChange={e => setWfName(e.target.value)}
          style={{ ...inputStyle, width: 180 }}
        />
        <select value={wfDept} onChange={e => setWfDept(e.target.value)} style={{ ...selectStyle, width: 140 }}>
          <option value="">Department</option>
          {DEPARTMENTS.map(d => <option key={d} value={d}>{humanize(d)}</option>)}
        </select>
        <select value={wfTrigger} onChange={e => setWfTrigger(e.target.value)} style={{ ...selectStyle, width: 120 }}>
          {TRIGGERS.map(t => <option key={t} value={t}>{humanize(t)}</option>)}
        </select>

        <div style={{ flex: 1 }} />

        <button onClick={handleValidate} style={{
          background: 'rgba(139,92,246,0.15)', border: '1px solid rgba(139,92,246,0.3)',
          borderRadius: 6, padding: '5px 14px', fontSize: 11, color: '#A78BFA',
          cursor: 'pointer', fontWeight: 600,
        }}>Validate</button>
        <button onClick={handleSaveDraft} disabled={saving} style={{
          background: 'var(--accent-border)', border: '1px solid var(--accent-border)',
          borderRadius: 6, padding: '5px 14px', fontSize: 11, color: 'var(--accent)',
          cursor: saving ? 'wait' : 'pointer', fontWeight: 600,
          opacity: saving ? 0.6 : 1,
        }}>{saving ? 'Saving...' : 'Save Draft'}</button>
        <button onClick={handleClear} style={{
          background: 'rgba(239,68,68,0.1)', border: '1px solid rgba(239,68,68,0.2)',
          borderRadius: 6, padding: '5px 14px', fontSize: 11, color: '#fca5a5',
          cursor: 'pointer', fontWeight: 500,
        }}>Clear</button>
      </div>

      {/* Validation + save feedback */}
      {(validationResults || saveResult) && (
        <div style={{ padding: '8px 16px', background: 'var(--bg-elevated, var(--bg-elevated))', borderBottom: '1px solid var(--border-subtle)', display: 'flex', gap: 16, flexWrap: 'wrap', alignItems: 'center' }}>
          {validationResults && validationResults.map((r, i) => (
            <span key={i} style={{ fontSize: 11, color: r.pass ? '#10B981' : '#ef4444' }}>
              {r.pass ? '\u2713' : '\u2717'} {r.label}
            </span>
          ))}
          {saveResult && (
            <span style={{ fontSize: 11, fontWeight: 600, color: saveResult.ok ? '#10B981' : '#ef4444', marginLeft: 'auto' }}>
              {saveResult.message}
            </span>
          )}
        </div>
      )}

      {/* Main area: palette + canvas + config panel */}
      <div style={{ flex: 1, display: 'flex', overflow: 'hidden', border: '1px solid var(--border-subtle)', borderTop: 'none', borderRadius: '0 0 8px 8px' }}>

        {/* Left: Skill Palette */}
        <div style={{
          width: 220, borderRight: '1px solid var(--border-subtle)',
          background: 'var(--bg-surface, var(--bg-surface))', overflowY: 'auto', padding: '8px 0',
          flexShrink: 0,
        }}>
          <div style={{ padding: '8px 14px', fontSize: 11, fontWeight: 700, color: 'var(--text-primary)', textTransform: 'uppercase' as const, letterSpacing: '0.5px' }}>
            Skill Palette
          </div>
          <div style={{ padding: '0 14px 8px', fontSize: 10, color: 'var(--text-hint)' }}>
            Drag skills onto the canvas
          </div>

          {skillsLoading ? (
            <div style={{ padding: 20, fontSize: 11, color: 'var(--text-hint)', textAlign: 'center' }}>Loading skills...</div>
          ) : (
            <>
              {groupedSkills.map(([cat, skills]) => (
                <div key={cat}>
                  <div
                    onClick={() => setExpandedCats(prev => {
                      const next = new Set(prev);
                      if (next.has(cat)) next.delete(cat); else next.add(cat);
                      return next;
                    })}
                    style={{
                      padding: '8px 14px', fontSize: 10, fontWeight: 700,
                      color: ENGINE_COLORS[cat] || 'var(--text-secondary)',
                      cursor: 'pointer', display: 'flex', justifyContent: 'space-between', alignItems: 'center',
                      textTransform: 'uppercase' as const, letterSpacing: '0.5px',
                    }}
                  >
                    <span>{cat} ({skills.length})</span>
                    <span style={{ fontSize: 8, color: 'var(--text-hint)' }}>{expandedCats.has(cat) ? '\u25BC' : '\u25B6'}</span>
                  </div>
                  {expandedCats.has(cat) && skills.slice(0, 20).map((skill: any) => (
                    <div
                      key={skill.skill_code}
                      draggable
                      onDragStart={e => onPaletteDragStart(e, skill)}
                      style={{
                        padding: '6px 14px 6px 20px', fontSize: 11,
                        color: 'var(--text-secondary)', cursor: 'grab',
                        borderLeft: `2px solid transparent`,
                        transition: 'all 0.1s',
                        overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' as const,
                      }}
                      title={skill.description || skill.skill_name}
                      onMouseEnter={e => { e.currentTarget.style.background = 'rgba(0,0,0,0.04)'; e.currentTarget.style.borderLeftColor = ENGINE_COLORS[cat] || '#64748B'; }}
                      onMouseLeave={e => { e.currentTarget.style.background = 'transparent'; e.currentTarget.style.borderLeftColor = 'transparent'; }}
                    >
                      {skill.skill_name || humanize(skill.skill_code)}
                    </div>
                  ))}
                </div>
              ))}

              {/* Tier 2 notice */}
              <div style={{ margin: '16px 14px', padding: '12px', background: 'var(--accent-subtle)', border: '1px solid rgba(139,92,246,0.15)', borderRadius: 8 }}>
                <div style={{ fontSize: 10, fontWeight: 600, color: '#A78BFA', marginBottom: 6 }}>
                  Need advanced capabilities?
                </div>
                <div style={{ fontSize: 10, color: 'var(--text-hint)', lineHeight: 1.5, marginBottom: 8 }}>
                  RESOLVE, RESEARCH, VALIDATE, and DETECT engines require professional configuration for SOX/GAAP/IFRS compliance.
                </div>
                <button
                  onClick={() => setShowRequestForm(true)}
                  style={{
                    background: 'rgba(139,92,246,0.2)', border: '1px solid rgba(139,92,246,0.3)',
                    borderRadius: 6, padding: '5px 12px', fontSize: 10, color: '#A78BFA',
                    cursor: 'pointer', fontWeight: 600, width: '100%',
                  }}
                >
                  Request Workflow &rarr;
                </button>
              </div>
            </>
          )}
        </div>

        {/* Center: Canvas */}
        <div style={{ flex: 1 }}>
          <ReactFlow
            nodes={nodes}
            edges={edges}
            onNodesChange={onNodesChange}
            onEdgesChange={onEdgesChange}
            onConnect={onConnect}
            onDrop={onDrop}
            onDragOver={onDragOver}
            nodeTypes={nodeTypes}
            fitView
            fitViewOptions={{ padding: 0.3 }}
            proOptions={{ hideAttribution: true }}
            style={{ background: 'var(--bg-base, var(--bg-base))' }}
            deleteKeyCode="Delete"
          >
            <Background color="rgba(0,0,0,0.02)" gap={24} />
            <MiniMap
              style={{ background: 'var(--bg-surface, var(--bg-surface))', border: '1px solid rgba(0,0,0,0.06)', borderRadius: 6 }}
              nodeColor={(node: Node) => (node.data as any)?.categoryColor || '#64748B'}
              maskColor="rgba(10, 22, 40, 0.7)"
            />
            <Controls style={{ background: 'var(--bg-surface, var(--bg-surface))', border: '1px solid rgba(0,0,0,0.06)', borderRadius: 6 }} />
          </ReactFlow>
        </div>

        {/* Right: Config Panel */}
        {selectedNodeId && selectedData && (
          <div style={{
            width: 260, borderLeft: '1px solid var(--border-subtle)',
            padding: 16, background: 'var(--bg-surface, var(--bg-surface))', overflowY: 'auto',
            flexShrink: 0,
          }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 12 }}>
              <span style={{ fontSize: 9, textTransform: 'uppercase' as const, letterSpacing: '0.5px', fontWeight: 600, color: selectedData.categoryColor }}>
                {selectedData.category}
              </span>
              <button onClick={() => setSelectedNodeId(null)} style={{
                background: 'none', border: 'none', color: 'var(--text-hint)',
                cursor: 'pointer', fontSize: 14, lineHeight: 1,
              }}>&times;</button>
            </div>
            <div style={{ fontSize: 14, fontWeight: 600, color: 'var(--text-primary)', marginBottom: 4 }}>
              {selectedData.skillName}
            </div>
            <div style={{ fontSize: 10, fontFamily: 'var(--font-mono)', color: 'var(--text-hint)', marginBottom: 12 }}>
              {selectedData.skillCode}
            </div>

            <div style={{ borderTop: '1px solid var(--border-subtle)', paddingTop: 12 }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 8 }}>
                <span style={{ fontSize: 10, color: 'var(--text-muted)' }}>Step</span>
                <span style={{ fontSize: 10, fontFamily: 'var(--font-mono)', fontWeight: 500, color: 'var(--text-primary)' }}>#{selectedData.stepNumber}</span>
              </div>
              <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 8 }}>
                <span style={{ fontSize: 10, color: 'var(--text-muted)' }}>Engine</span>
                <span style={{ fontSize: 10, fontFamily: 'var(--font-mono)', fontWeight: 500, color: selectedData.categoryColor }}>{selectedData.category}</span>
              </div>

              {/* Outgoing connections */}
              <div style={{ marginTop: 12 }}>
                <div style={{ fontSize: 9, fontWeight: 600, color: 'var(--text-hint)', textTransform: 'uppercase' as const, marginBottom: 6 }}>Connections</div>
                {edges.filter(e => e.source === selectedNodeId).map(e => {
                  const targetNode = nodes.find(n => n.id === e.target);
                  const targetName = e.target === 'END' ? 'END' : (targetNode?.data as any)?.skillName || e.target;
                  return (
                    <div key={e.id} style={{ fontSize: 10, color: '#10B981', marginBottom: 4 }}>
                      &rarr; {targetName}
                    </div>
                  );
                })}
                {edges.filter(e => e.source === selectedNodeId).length === 0 && (
                  <div style={{ fontSize: 10, color: 'var(--text-hint)', fontStyle: 'italic' }}>No outgoing connections</div>
                )}
              </div>
            </div>

            {/* Delete button */}
            <button
              onClick={() => deleteNode(selectedNodeId)}
              style={{
                marginTop: 20, width: '100%',
                background: 'rgba(239,68,68,0.1)', border: '1px solid rgba(239,68,68,0.2)',
                borderRadius: 6, padding: '8px 14px', fontSize: 11, color: '#fca5a5',
                cursor: 'pointer', fontWeight: 600,
              }}
            >
              Delete Node
            </button>
          </div>
        )}
      </div>

      {/* Workflow Description (collapsible) */}
      <div style={{ padding: '8px 16px', background: 'var(--bg-surface)', borderTop: '1px solid var(--border-subtle)' }}>
        <input
          placeholder="Workflow description (optional)"
          value={wfDesc}
          onChange={e => setWfDesc(e.target.value)}
          style={{ ...inputStyle, fontSize: 11 }}
        />
      </div>

      {/* Request Workflow Modal */}
      {showRequestForm && (
        <div style={{
          position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.15)',
          display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000,
        }} onClick={() => setShowRequestForm(false)}>
          <div onClick={e => e.stopPropagation()} style={{
            background: 'var(--bg-surface, var(--bg-surface))', borderRadius: 12, padding: 24,
            width: 420, border: '1px solid var(--border-subtle)',
          }}>
            <div style={{ fontSize: 16, fontWeight: 700, color: 'var(--text-primary)', marginBottom: 4 }}>
              Request Professional Workflow
            </div>
            <div style={{ fontSize: 11, color: 'var(--text-hint)', marginBottom: 16 }}>
              SOX, GAAP, IFRS workflows require Agent Workforce team configuration.
            </div>

            <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
              <input placeholder="Workflow name" value={reqName} onChange={e => setReqName(e.target.value)} style={inputStyle} />
              <select value={reqDept} onChange={e => setReqDept(e.target.value)} style={selectStyle}>
                <option value="">Department</option>
                {DEPARTMENTS.map(d => <option key={d} value={d}>{humanize(d)}</option>)}
              </select>
              <textarea
                placeholder="Describe what the workflow should do..."
                value={reqDesc}
                onChange={e => setReqDesc(e.target.value)}
                rows={4}
                style={{ ...inputStyle, resize: 'vertical' as const }}
              />
              <div style={{ fontSize: 10, color: 'var(--text-hint)' }}>Capabilities needed:</div>
              <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
                {['RESOLVE', 'RESEARCH', 'VALIDATE', 'DETECT', 'CALCULATE'].map(cap => (
                  <label key={cap} style={{ fontSize: 10, color: ENGINE_COLORS[cap], cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 4 }}>
                    <input
                      type="checkbox"
                      checked={reqCaps.includes(cap)}
                      onChange={e => {
                        if (e.target.checked) setReqCaps(prev => [...prev, cap]);
                        else setReqCaps(prev => prev.filter(c => c !== cap));
                      }}
                    />
                    {cap}
                  </label>
                ))}
              </div>
            </div>

            {reqResult && <div style={{ marginTop: 12, fontSize: 11, color: reqResult.includes('success') ? '#10B981' : '#ef4444' }}>{reqResult}</div>}

            <div style={{ display: 'flex', gap: 8, marginTop: 16, justifyContent: 'flex-end' }}>
              <button onClick={() => setShowRequestForm(false)} style={{
                background: 'transparent', border: '1px solid var(--border-default)',
                borderRadius: 6, padding: '6px 16px', fontSize: 11, color: 'var(--text-secondary)', cursor: 'pointer',
              }}>Cancel</button>
              <button
                onClick={handleRequestSubmit}
                disabled={reqSubmitting || !reqName.trim() || !reqDesc.trim() || !reqDept}
                style={{
                  background: 'rgba(139,92,246,0.2)', border: '1px solid rgba(139,92,246,0.3)',
                  borderRadius: 6, padding: '6px 16px', fontSize: 11, color: '#A78BFA',
                  cursor: 'pointer', fontWeight: 600,
                  opacity: (!reqName.trim() || !reqDesc.trim() || !reqDept) ? 0.5 : 1,
                }}
              >{reqSubmitting ? 'Submitting...' : 'Submit Request'}</button>
            </div>
          </div>
        </div>
      )}

      {/* React Flow dark theme overrides */}
      <style>{`
        .react-flow__controls-button {
          background: var(--bg-surface, var(--bg-surface)) !important;
          color: var(--text-secondary, #94A3B8) !important;
          border-color: rgba(255,255,255, 0.06) !important;
          fill: var(--text-secondary, #94A3B8) !important;
        }
        .react-flow__controls-button:hover {
          background: var(--bg-elevated, var(--bg-elevated)) !important;
        }
        .react-flow__controls-button svg {
          fill: var(--text-secondary, #94A3B8) !important;
        }
        .react-flow__edge-textbg { rx: 4; }
      `}</style>
    </div>
  );
}

// ─── Wrapper with ReactFlowProvider ──────────────────────────

export default function WorkflowBuilder({ onClose }: { onClose: () => void }) {
  return (
    <ReactFlowProvider>
      <WorkflowBuilderInner onClose={onClose} />
    </ReactFlowProvider>
  );
}
